/**
 * Custom Predictive Search
 * ------------------------------------------------------------------
 * Talks to Shopify's real /search route via the Section Rendering API
 * (not the Predictive Search API, which hard-caps at 10 results per
 * resource type with no way to raise it) so that results are
 * server-rendered through sections/predictive-search-results.liquid
 * (which reuses the real `custom-product-card` snippet and reads
 * genuine product.collections data — neither is possible from the
 * plain /search/suggest.json endpoint alone) with every matching
 * product shown (up to 250 per request, Shopify's own ceiling).
 *
 * Collections work the same way for the first 250, rendered
 * synchronously as part of the same request. A store with more than
 * 250 total collections gets the rest via fetchMoreCollections, a
 * separate, purely-additive request (see
 * sections/predictive-search-collections-more.liquid for why it can't
 * just be folded into the request above) that only ever appends to
 * what's already on screen — it never replaces or clears anything, so
 * it can fail without taking the already-rendered matches down with it.
 *
 * NOTE ON "EMPTY INPUT" BEHAVIOUR:
 * The written spec says to hide/close the dropdown whenever the input
 * is empty. The reference screenshots show something more useful on
 * focus-with-empty-input: a "Popular Choices" + "Recommended For You"
 * panel. This implementation follows the screenshots (default panel
 * shown on focus, not a hard close) since that matches real production
 * behaviour and is better UX. If you want the literal spec behaviour
 * instead, swap the `this.showDefaultState()` calls below for
 * `this.close()`.
 * ------------------------------------------------------------------
 */

// Matches the rendering order in sections/predictive-search-results.liquid
// and sections/predictive-search-collections-more.liquid: merchant-set
// priority (product/collection.metafields.custom.search_priority) first,
// then title-starts-with-the-term, then everything else. Used by
// appendMoreCollections to insert later-page matches at the right rank
// instead of always at the end.
const COLLECTIONS_TIER_ORDER = ['priority', 'prefix', 'contains'];

// Module-level (not per-instance) so the desktop pill and the mobile
// takeover — two separate CustomPredictiveSearch elements on one page —
// share a single load, instead of each racing to inject its own copy of
// the script the first time either one's input is focused.
let searchSynonymsLoadPromise = null;
function loadSearchSynonyms() {
  if (searchSynonymsLoadPromise) return searchSynonymsLoadPromise;
  searchSynonymsLoadPromise = new Promise((resolve) => {
    if (typeof window.translateSearchQuery === 'function') return resolve();
    const url = window.routes && window.routes.search_synonyms_url;
    if (!url) return resolve(); // no URL to load from — fetchResults' own
    // `window.translateSearchQuery ? ... : term` fallback still applies.
    const script = document.createElement('script');
    script.src = url;
    script.onload = resolve;
    script.onerror = resolve; // fetchResults' fallback covers this too — never block search on it.
    document.head.appendChild(script);
  });
  return searchSynonymsLoadPromise;
}

class CustomPredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.input = this.querySelector('[data-predictive-search-input]');
    this.form = this.querySelector('.cps__form');
    this.dropdown = this.querySelector('[data-predictive-search-dropdown]');
    this.defaultState = this.querySelector('[data-default-state]');
    this.loadingState = this.querySelector('[data-loading-state]');
    this.errorState = this.querySelector('[data-error-state]');
    this.resultsMount = this.querySelector('[data-predictive-search-results]');
    this.clearBtn = this.querySelector('[data-clear-search]');
    this.statusEl = this.querySelector('[data-predictive-search-status]');
    this.recommendedSection = this.querySelector('[data-recommended-section]');
    this.recommendedMount = this.querySelector('[data-recommended-mount]');
    this.recommendedTrack = this.querySelector('[data-recommended-track]');
    this.recommendedPrevBtn = this.querySelector('[data-recommended-prev]');
    this.recommendedNextBtn = this.querySelector('[data-recommended-next]');
    this.recommendedProgress = this.querySelector('[data-recommended-progress]');
    this.recommendedProgressBar = this.querySelector('[data-recommended-progress-bar]');
    this.recentSearchesSection = this.querySelector('[data-recent-searches-section]');
    this.recentSearchesList = this.querySelector('[data-recent-searches-list]');
    this.clearRecentSearchesBtn = this.querySelector('[data-clear-recent-searches]');

    this.sectionId = 'predictive-search-results';
    this.countSectionId = 'predictive-search-count';
    this.moreCollectionsSectionId = 'predictive-search-collections-more';
    this.cache = new Map(); // term -> rendered HTML string
    this.countCache = new Map(); // term -> total match count
    this.moreCollectionsCache = new Map(); // term -> extra <a> markup string (pages 2+)
    this.abortController = null;
    this.debounceTimer = null;
    this.debounceDelay = 250;
    this.activeIndex = -1;
    this.lastTerm = '';

    // Shared with assets/search-recent-searches.js (legacy search modal) so recent
    // searches don't fragment across the theme's different predictive-search UIs.
    this.recentSearchesKey = 'themex-recent-searches';
    this.recentSearchesLimit = 5;

    this.init();
  }

  init() {
    this.bindEvents();
    this.bindRecommendedSlider();
    this.renderRecentSearches();
  }

  bindEvents() {
    this.input.addEventListener('input', this.onInput.bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.input.addEventListener('keydown', this.onKeyDown.bind(this));
    this.form.addEventListener('submit', () => this.saveRecentSearch(this.input.value));

    if (this.clearBtn) {
      this.clearBtn.addEventListener('click', this.onClearClick.bind(this));
    }

    if (this.clearRecentSearchesBtn) {
      this.clearRecentSearchesBtn.addEventListener('click', this.onClearRecentSearchesClick.bind(this));
    }

    // Quick-search pills in the default state (both "Popular Choices" term
    // pills and "Recent Searches" items share this attribute). Their results
    // skip the collections column — only the products list is relevant here.
    this.addEventListener('click', (event) => {
      const pill = event.target.closest('[data-quick-term]');
      if (!pill) return;
      const term = pill.getAttribute('data-quick-term');
      this.input.value = term;
      this.input.focus();
      this.toggleClearButton(term);
      this.saveRecentSearch(term);
      this.fetchResults(term).then(() => this.hideCollectionsResults());
    });

    // Any real navigation out of the dropdown (collection suggestion, "View
    // all", a product card) is a completed search — worth remembering too.
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-clear-recent-searches]')) return;
      const link = event.target.closest('a[href]');
      if (!link) return;
      this.saveRecentSearch(this.input.value);
    });

    document.addEventListener('click', this.onDocumentClick.bind(this));
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.close();
    });
  }

  onFocus() {
    // Fire-and-forget: kicks off the lazy load the moment the shopper
    // shows intent to search, well before the 250ms debounce on any
    // fetch that would actually need window.translateSearchQuery ready.
    // Not awaited — fetchResults()/fetchResultsCount() already fall back
    // to the untranslated term if this hasn't resolved yet by then.
    loadSearchSynonyms();

    const term = this.input.value.trim();
    this.open();
    if (term.length === 0) {
      this.showDefaultState();
    } else if (this.cache.has(term)) {
      this.renderResultsHTML(this.cache.get(term));
      this.fetchResultsCount(term);
    } else {
      this.fetchResults(term);
    }
  }

  onInput(event) {
    const term = event.target.value.trim();
    this.toggleClearButton(term);

    if (term.length === 0) {
      clearTimeout(this.debounceTimer);
      this.showDefaultState();
      return;
    }

    this.debounce(() => this.fetchResults(term), this.debounceDelay);
  }

  onClearClick() {
    clearTimeout(this.debounceTimer);
    this.input.value = '';
    this.toggleClearButton('');
    this.showDefaultState();
    this.input.focus();
  }

  onDocumentClick(event) {
    if (!this.contains(event.target)) this.close();
  }

  onKeyDown(event) {
    const items = Array.from(this.querySelectorAll('[data-predictive-search-item]'))
      .filter((item) => item.offsetParent !== null);

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.activeIndex = Math.min(this.activeIndex + 1, items.length - 1);
        this.focusItem(items);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.activeIndex = Math.max(this.activeIndex - 1, 0);
        this.focusItem(items);
        break;
      case 'Enter':
        if (this.activeIndex >= 0 && items[this.activeIndex]) {
          event.preventDefault();
          items[this.activeIndex].click();
        }
        break;
      case 'Escape':
        this.close();
        break;
      default:
        break;
    }
  }

  focusItem(items) {
    items.forEach((item, index) => {
      item.classList.toggle('is-active', index === this.activeIndex);
    });
    const active = items[this.activeIndex];
    if (active) active.scrollIntoView({ block: 'nearest' });
  }

  /** Reads the recent-searches list from localStorage (newest first). */
  getRecentSearches() {
    try {
      const stored = JSON.parse(localStorage.getItem(this.recentSearchesKey));
      return Array.isArray(stored) ? stored : [];
    } catch (error) {
      return [];
    }
  }

  /**
   * Records a completed search term, deduping case-insensitively and moving
   * it to the front. Also drops any older entry that's a substring of the
   * new term (e.g. saving "blue shirt" removes a previously saved "blue"),
   * matching the behaviour already established in the legacy search modal
   * (assets/search-recent-searches.js) so both UIs feel consistent.
   */
  saveRecentSearch(term) {
    term = (term || '').trim();
    if (!term) return;

    let searches = this.getRecentSearches().filter(
      (saved) => saved.toLowerCase() !== term.toLowerCase()
    );
    searches = searches.filter((saved) => !term.toLowerCase().includes(saved.toLowerCase()));
    searches.unshift(term);

    try {
      localStorage.setItem(
        this.recentSearchesKey,
        JSON.stringify(searches.slice(0, this.recentSearchesLimit))
      );
    } catch (error) {
      // Storage unavailable (private browsing, quota) — recent searches just won't persist.
    }

    this.renderRecentSearches();
  }

  renderRecentSearches() {
    if (!this.recentSearchesSection || !this.recentSearchesList) return;

    const searches = this.getRecentSearches();
    this.recentSearchesSection.hidden = searches.length === 0;
    this.recentSearchesList.innerHTML = searches
      .map((term) => {
        const safeTerm = this.escapeHTML(term);
        return `<button type="button" class="cps-recent-searches__item" data-quick-term="${safeTerm}">${safeTerm}</button>`;
      })
      .join('');
  }

  onClearRecentSearchesClick() {
    try {
      localStorage.removeItem(this.recentSearchesKey);
    } catch (error) {
      // Storage unavailable — nothing to clear.
    }
    this.renderRecentSearches();
  }

  escapeHTML(value) {
    const div = document.createElement('div');
    div.textContent = value;
    return div.innerHTML;
  }

  /** Debounce helper (250ms default, per spec). */
  debounce(callback, delay) {
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(callback, delay);
  }

  /** "Recommended for you" horizontal slider: prev/next arrows + scroll progress bar. */
  bindRecommendedSlider() {
    if (!this.recommendedTrack) return;

    const SCROLL_AMOUNT = 300;

    if (this.recommendedPrevBtn) {
      this.recommendedPrevBtn.addEventListener('click', () => {
        this.recommendedTrack.scrollBy({ left: -SCROLL_AMOUNT, behavior: 'smooth' });
      });
    }

    if (this.recommendedNextBtn) {
      this.recommendedNextBtn.addEventListener('click', () => {
        this.recommendedTrack.scrollBy({ left: SCROLL_AMOUNT, behavior: 'smooth' });
      });
    }

    this.recommendedTrack.addEventListener('scroll', this.updateRecommendedSliderState.bind(this));
    window.addEventListener('resize', this.updateRecommendedSliderState.bind(this));
  }

  updateRecommendedSliderState() {
    const track = this.recommendedTrack;
    if (!track) return;

    const maxScroll = track.scrollWidth - track.clientWidth;
    const hasOverflow = maxScroll > 4;

    if (this.recommendedPrevBtn) {
      this.recommendedPrevBtn.classList.toggle('is-visible', hasOverflow && track.scrollLeft > 4);
    }
    if (this.recommendedNextBtn) {
      this.recommendedNextBtn.classList.toggle('is-visible', hasOverflow && track.scrollLeft < maxScroll - 4);
    }

    if (this.recommendedProgress) {
      this.recommendedProgress.hidden = !hasOverflow;
    }

    if (this.recommendedProgressBar && hasOverflow) {
      const visibleRatio = track.clientWidth / track.scrollWidth;
      const scrollRatio = maxScroll > 0 ? track.scrollLeft / maxScroll : 0;
      this.recommendedProgressBar.style.width = `${visibleRatio * 100}%`;
      this.recommendedProgressBar.style.left = `${(100 - visibleRatio * 100) * scrollRatio}%`;
    }
  }

  /**
   * Core fetch: hits the Section Rendering variant of the real /search route.
   * Deliberately not the Predictive Search API — that API hard-caps at 10
   * results per resource type (products, collections), which can't be raised.
   * sections/predictive-search-results.liquid sources products from
   * `search.results` and collections from the store's full `collections`
   * list, both explicitly wrapped in `{% paginate %} by 250` there (see
   * that file for why a URL param alone doesn't achieve this).
   */
  async fetchResults(term) {
    if (!term) return;

    // Stale-call guard: the input may have been cleared since this was scheduled.
    if (this.input.value.trim() !== term) return;

    // Fire-and-forget: powers the "View All (N)" count once it resolves.
    this.fetchResultsCount(term);

    // Avoid re-fetching an identical, already-cached keyword.
    if (this.cache.has(term)) {
      this.renderResultsHTML(this.cache.get(term));
      return;
    }

    // Cancel any in-flight request before starting a new one.
    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();

    this.showLoader();
    this.lastTerm = term;

    // Translate a recognised local-language query to English *before* it
    // reaches Shopify (see assets/search-synonyms.js for why — Shopify's
    // own /search candidate pool for a non-Latin-script query turned out
    // to be irrelevant noise when tested directly, so narrowing it
    // afterward in Liquid can never recover the right products). `term`
    // itself is left untranslated everywhere else in this class — cache
    // keys, staleness checks, the input box — only the text actually sent
    // to Shopify changes.
    const translatedTerm = window.translateSearchQuery ? window.translateSearchQuery(term) : term;

    const params = new URLSearchParams({
      q: translatedTerm,
      type: 'product',
      // Raising `search.results` above its unpaginated default happens in
      // Liquid, via `{% paginate search.results by 250 %}` — there's no
      // documented `page_size` (or similar) URL param for /search itself.
      //
      // 'last' (not 'hide'): sold-out products still need to reach
      // search.results at all, or sections/predictive-search-results.liquid
      // has nothing to render in its sold-out pass (see
      // snippets/predictive-search-product-tier.liquid) — a shopper
      // searching a sold-out product by name should still find it, just
      // ranked behind anything actually purchasable right now.
      'options[unavailable_products]': 'last',
      section_id: this.sectionId,
    });

    const url = `${window.routes.search_url}?${params.toString()}`;

    try {
      const response = await fetch(url, { signal: this.abortController.signal });
      const text = await response.text();

      if (!response.ok) {
        throw new Error(`${response.status}: ${text}`);
      }

      // Bail out if the user has since changed the input (stale response guard).
      if (this.input.value.trim() !== term) return;

      // sections/predictive-search-results.liquid wraps its real output in a
      // `hidden` [data-predictive-search-payload] div — that keeps it safely
      // invisible on the rare occasion this section renders as part of a real
      // page load (see that file's comment) rather than this AJAX fetch.
      // Reading that div's innerHTML (not the section wrapper's own
      // outerHTML/innerHTML) skips right past its `hidden` attribute, so the
      // dropdown always gets the real, unwrapped content either way.
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const sectionEl = doc.querySelector(`#shopify-section-${this.sectionId}`);
      const payloadEl = sectionEl ? sectionEl.querySelector('[data-predictive-search-payload]') : null;
      const markup = payloadEl ? payloadEl.innerHTML : '';

      this.cacheResults(term, markup);
      this.renderResultsHTML(markup);
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('[CustomPredictiveSearch] fetch error:', error);
      this.showError();
    } finally {
      this.hideLoader();
    }
  }

  cacheResults(term, html) {
    this.cache.set(term, html);
  }

  /**
   * fetchResults (above) already computes an accurate title-matched count
   * from its own up-to-250-result /search request, exposed via
   * `data-exact-count` — see applyViewAllCount. This second, independent
   * /search request (against sections/predictive-search-count.liquid,
   * re-deriving the count the same way) exists as a cross-check so the
   * "View All (N)" badge never under-reports.
   */
  async fetchResultsCount(term) {
    if (!term) return;

    if (this.countCache.has(term)) {
      this.applyViewAllCount(term, this.countCache.get(term));
      return;
    }

    // Same query translation as fetchResults above, and for the same
    // reason — this count must be derived from the same (translated)
    // search Shopify actually ran, or it'll disagree with the results
    // list. `term` (untranslated) stays the cache key / staleness value.
    const translatedTerm = window.translateSearchQuery ? window.translateSearchQuery(term) : term;

    const params = new URLSearchParams({
      q: translatedTerm,
      type: 'product',
      section_id: this.countSectionId,
    });
    const url = `${window.routes.search_url}?${params.toString()}`;

    try {
      const response = await fetch(url);
      const text = await response.text();

      if (!response.ok) {
        throw new Error(`${response.status}: ${text}`);
      }

      const doc = new DOMParser().parseFromString(text, 'text/html');
      const el = doc.querySelector('[data-predictive-search-count-value]');
      const count = el ? parseInt(el.textContent, 10) || 0 : 0;

      this.countCache.set(term, count);
      this.applyViewAllCount(term, count);
    } catch (error) {
      console.error('[CustomPredictiveSearch] count fetch error:', error);
    }
  }

  /**
   * `count` (from fetchResultsCount) and `data-exact-count` (from this
   * same request's rendered results, set by predictive-search-results.liquid)
   * are both real title-matched counts over up to 250 /search results, so
   * they should normally agree — but each is a separate request, so taking
   * the max against `visibleCount` (what's actually rendered) keeps the
   * badge from ever contradicting the list sitting right above it. This
   * runs per custom-element instance, so it applies to both the desktop
   * (CustomPredictiveSearch-input-desktop) and mobile
   * (CustomPredictiveSearch-input-mobile) inputs the same way.
   */
  applyViewAllCount(term, count) {
    if (this.input.value.trim() !== term) return;
    const visibleCount = this.resultsMount.querySelectorAll('.cps-products__item').length;
    const countEl = this.resultsMount.querySelector('[data-view-all-count]');
    if (!countEl) return;
    const exactCount = parseInt(countEl.dataset.exactCount, 10) || 0;
    countEl.textContent = ` (${Math.max(count, visibleCount, exactCount)})`;
  }

  renderResultsHTML(html) {
    this.hideLoader();
    this.errorState.hidden = true;
    this.defaultState.hidden = true;
    this.resultsMount.innerHTML = html;
    this.resultsMount.hidden = false;
    this.activeIndex = -1;

    const count = this.resultsMount.querySelectorAll('.cps-products__item').length;
    if (this.statusEl) {
      this.statusEl.textContent = count > 0
        ? `${count} results found`
        : 'No products found';
    }

    const term = this.input.value.trim();
    // Seed the badge with what's actually rendered right away; the async
    // fetchResultsCount() call (if it resolves with a bigger number) will
    // raise it further via applyViewAllCount's max(), never lower it.
    this.applyViewAllCount(term, this.countCache.get(term) || 0);

    // Page 1's collection matches are already in the markup above,
    // rendered synchronously — this only ever adds more on top for stores
    // with more than 250 total collections. If it fails, page 1's matches
    // (already on screen) are unaffected.
    this.fetchMoreCollections(term);

    this.open();
  }

  /**
   * Fetches collection matches beyond page 1 (which
   * sections/predictive-search-results.liquid already rendered
   * synchronously) — see sections/predictive-search-collections-more.liquid
   * for why this has to be a separate request. Purely additive: appends to
   * [data-collections-list], never replaces or clears it, so any failure
   * here just means the extra pages don't show up — page 1's matches stay
   * exactly as already rendered.
   */
  async fetchMoreCollections(term) {
    if (!term) return;

    const column = this.resultsMount.querySelector('[data-collections-column]');
    if (!column) return;

    const totalPages = parseInt(column.dataset.collectionsTotalPages, 10) || 1;
    if (totalPages <= 1) return;

    if (this.moreCollectionsCache.has(term)) {
      this.appendMoreCollections(term, this.moreCollectionsCache.get(term));
      return;
    }

    try {
      const pageFetches = [];
      for (let page = 2; page <= totalPages; page += 1) {
        const params = new URLSearchParams({
          q: term,
          section_id: this.moreCollectionsSectionId,
          page: String(page),
        });
        pageFetches.push(
          fetch(`${window.routes.search_url}?${params.toString()}`)
            .then((response) => response.text())
            .then((html) => {
              const doc = new DOMParser().parseFromString(html, 'text/html');
              const sectionEl = doc.querySelector(`#shopify-section-${this.moreCollectionsSectionId}`);
              return sectionEl ? Array.from(sectionEl.querySelectorAll('[data-collection-handle]')) : [];
            })
            .catch(() => [])
        );
      }

      const pages = await Promise.all(pageFetches);
      const items = pages.flat();

      // Built via real DOM nodes (not string interpolation) so the browser
      // handles escaping collection titles/handles/URLs correctly.
      const markup = items
        .map((anchorEl) => {
          const li = document.createElement('li');
          li.dataset.collectionHandle = anchorEl.dataset.collectionHandle;
          li.dataset.matchType = anchorEl.dataset.matchType;
          li.className = anchorEl.dataset.isPredictive === 'true'
            ? 'cps-collections__item cps-collections__item--predictive'
            : 'cps-collections__item';
          const anchor = document.createElement('a');
          anchor.href = anchorEl.getAttribute('href');
          anchor.className = 'cps-collections__link';
          anchor.setAttribute('data-predictive-search-item', '');
          anchor.textContent = anchorEl.textContent.trim();
          li.appendChild(anchor);
          return li.outerHTML;
        })
        .join('');

      this.moreCollectionsCache.set(term, markup);
      this.appendMoreCollections(term, markup);
    } catch (error) {
      console.error('[CustomPredictiveSearch] more-collections fetch error:', error);
    }
  }

  /**
   * Appends fetchMoreCollections' extra matches, deduped against what's
   * already listed. Page 1 (sections/predictive-search-results.liquid)
   * always lists collections in COLLECTIONS_TIER_ORDER: merchant-set
   * priority tier first (data-match-type="priority"), then
   * title-starts-with-the-term (data-match-type="prefix"), then
   * contains-only matches last. A new match from a later page is inserted
   * right after the last existing item ranked at or above its own tier
   * (ahead of every lower-ranked item already shown), instead of just
   * tacked onto the end — so e.g. a title-starts-with-the-term collection
   * from page 3 still outranks a merely-contains-the-term collection from
   * page 1.
   */
  appendMoreCollections(term, markup) {
    if (this.input.value.trim() !== term) return;
    if (!markup) return;

    const list = this.resultsMount.querySelector('[data-collections-list]');
    if (!list) return;

    const existingHandles = new Set(
      Array.from(list.querySelectorAll('[data-collection-handle]')).map((el) => el.dataset.collectionHandle)
    );

    const tierRank = (li) => {
      const index = COLLECTIONS_TIER_ORDER.indexOf(li.dataset.matchType);
      return index === -1 ? COLLECTIONS_TIER_ORDER.length : index;
    };

    const wrapper = document.createElement('div');
    wrapper.innerHTML = markup;
    Array.from(wrapper.children).forEach((li) => {
      const handle = li.dataset.collectionHandle;
      if (handle && existingHandles.has(handle)) return;

      const rank = tierRank(li);
      const insertBeforeEl = Array.from(list.children).find((existing) => tierRank(existing) > rank);
      if (insertBeforeEl) {
        list.insertBefore(li, insertBeforeEl);
      } else {
        list.appendChild(li);
      }

      if (handle) existingHandles.add(handle);
    });
  }

  /**
   * Called after a "Popular Choices" pill click resolves. Hides just the
   * collections column of the just-rendered results — the products list
   * (`.cps-products`) is left exactly as rendered.
   */
  hideCollectionsResults() {
    const collectionsEl = this.resultsMount.querySelector('.cps-collections');
    if (collectionsEl) collectionsEl.hidden = true;

    const panelInner = this.resultsMount.querySelector('.cps-panel__inner');
    if (panelInner) panelInner.classList.add('cps-panel__inner--no-collections');
  }

  // Kept as named stubs to mirror the requested class structure.
  // Actual markup for these is produced server-side by
  // sections/predictive-search-results.liquid so `custom-product-card`
  // can be reused exactly as-is instead of being rebuilt in JS.
  renderCollections() {}
  renderProducts() {}
  renderViewAll() {}

  showLoader() {
    this.defaultState.hidden = true;
    this.errorState.hidden = true;
    this.resultsMount.hidden = true;
    this.loadingState.hidden = false;
  }

  hideLoader() {
    this.loadingState.hidden = true;
  }

  showError() {
    this.defaultState.hidden = true;
    this.resultsMount.hidden = true;
    this.errorState.hidden = false;
  }

  showDefaultState() {
    this.resultsMount.hidden = true;
    this.resultsMount.innerHTML = '';
    this.errorState.hidden = true;
    this.loadingState.hidden = true;
    this.defaultState.hidden = false;
    requestAnimationFrame(() => this.updateRecommendedSliderState());
  }

  toggleClearButton(term) {
    if (!this.clearBtn) return;
    this.clearBtn.hidden = term.length === 0;
  }

  open() {
    this.setAttribute('data-open', 'true');
    this.input.setAttribute('aria-expanded', 'true');
    this.dropdown.hidden = false;
  }

  close() {
    this.removeAttribute('data-open');
    this.input.setAttribute('aria-expanded', 'false');
    this.dropdown.hidden = true;
    this.activeIndex = -1;
  }
}

if (!customElements.get('custom-predictive-search')) {
  customElements.define('custom-predictive-search', CustomPredictiveSearch);
}
